/*eslint complexity: ["error", 35]*/
/*eslint max-lines-per-function: ["error", 240]*/
import React, { Dispatch, SetStateAction, useState } from 'react';

import {
  CreateUpdateContractDto,
  CreateUpdateContractEstimateItemDto,
  GetContractExtendedDto,
  GetEstimateItemContractDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import Button from '../../../common/components/Button/Button';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { ContractStatusEnum } from '../../../common/pokCore/validation/enums';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { useAuth } from '../../../common/hooks/useAuth';
import { validateToWaitingToSign } from '../../../common/pokCore/contexts/ContractContext';

import { ContractTabsEnum } from './ContractTabsEnum';
import { checkDivergedContractAmounts } from './checkDivergedContractAmounts';

interface ContractEstimateItemControlsProps {
  contract: CreateUpdateContractDto;
  extendedContract?: GetContractExtendedDto;
  setNotSavedTab: Dispatch<SetStateAction<ContractTabsEnum | undefined>>;
  notSavedTab?: ContractTabsEnum;
  handleRefresh: () => void;
  readOnly: boolean;
  setSaveErrors: Dispatch<SetStateAction<string[]>>;
  allAvailableEstimateItems: GetEstimateItemContractDto[];
  contractEstimateItems: CreateUpdateContractEstimateItemDto[];
  warnings: string[];
}

export const ContractEstimateItemControls: React.FC<
  ContractEstimateItemControlsProps
> = props => {
  const pok = usePokCore();
  const auth = useAuth();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('projectLng');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [isLoadingSigning, setIsLoadingSigning] = useState(false);
  const { hasNegativeAmounts, hasMoreThenAvailableAmounts } =
    checkDivergedContractAmounts(
      props.contractEstimateItems,
      props.allAvailableEstimateItems,
      props.extendedContract,
    );

  const id = props.extendedContract?.id || '';

  const handleSave = async () => {
    try {
      setIsLoading(true);
      await pok.contractsEstimateItems.updateEstimateItems(id, {
        contractEstimateItems: props.contractEstimateItems,
      });
      props.setSaveErrors([]);
      props.setNotSavedTab(undefined);
      await props.handleRefresh();
      notifications.saveCompleted();
    } catch (response) {
      props.setSaveErrors(await responseErrors(response as ResponseError));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveStatusContract = async () => {
    const status = await validateToWaitingToSign(props.contract);
    if (status.valid) {
      try {
        setIsLoadingStatus(true);
        await pok.contracts.waitingToSign(id);
        props.setSaveErrors([]);
        props.setNotSavedTab(undefined);
        await props.handleRefresh();
        notifications.saveCompleted();
      } catch (response) {
        props.setSaveErrors(await responseErrors(response as ResponseError));
      } finally {
        setIsLoadingStatus(false);
      }
    } else {
      props.setSaveErrors(status.errors);
    }
  };

  const signContracts = async () => {
    try {
      setIsLoadingSigning(true);
      const result = await pok.contracts.sign([id]);
      setIsLoadingSigning(false);
      if (result.errors && result.errors?.length > 0) {
        props.setSaveErrors(result.errors);
      } else {
        props.handleRefresh();
      }

      return {
        saved: true,
        errors: [],
      };
    } catch (response) {
      setIsLoadingSigning(false);
      props.setSaveErrors(await responseErrors(response as ResponseError));
    }
  };

  const unsignContracts = async (reason?: string) => {
    try {
      setIsLoadingSigning(true);
      const result = await pok.contracts.unsign([id], reason);
      setIsLoadingSigning(false);
      if (result.errors && result.errors?.length > 0) {
        props.setSaveErrors(result.errors);
      } else {
        props.handleRefresh();
      }

      return {
        saved: true,
        errors: [],
      };
    } catch (response) {
      setIsLoadingSigning(false);
      props.setSaveErrors(await responseErrors(response as ResponseError));
    }
  };

  const markSentByHand = async () => {
    try {
      await pok.contracts.markSentByHand(id);
      props.handleRefresh();
    } catch (response) {
      setIsLoadingSigning(false);
      props.setSaveErrors(await responseErrors(response as ResponseError));
    }
  };

  const handleMarkAsSignedContract = async () => {
    const status = await validateToWaitingToSign(props.contract);
    if (status.valid) {
      try {
        await pok.contracts.markAsSignedContract(id);
        props.setSaveErrors([]);
        props.setNotSavedTab(undefined);
        await props.handleRefresh();
        notifications.saveCompleted();
      } catch (response) {
        props.setSaveErrors(await responseErrors(response as ResponseError));
      }
    } else {
      props.setSaveErrors(status.errors);
    }
  };

  return (
    <div className="d-flex justify-content-end">
      {!props.warnings.length &&
        !props.readOnly &&
        (hasNegativeAmounts || hasMoreThenAvailableAmounts ? (
          <ConfirmationButton
            className="ms-2 mt-4"
            variant="primary"
            confirmation={
              hasNegativeAmounts
                ? t(tk.contract.hasNegativeAmounts, {
                    estimate: t(tk.prLang.estimate2),
                  })
                : t(tk.contract.hasMoreThenAvailableAmounts, {
                    estimate: t(tk.prLang.estimate2),
                  })
            }
            onOK={handleSave}
            isLoading={isLoading}
            modalSize="lg"
          >
            {t(tk.button.save)}
          </ConfirmationButton>
        ) : (
          <Button
            className="ms-2 mt-4"
            variant="primary"
            onClick={handleSave}
            isLoading={isLoading}
          >
            {t(tk.button.save)}
          </Button>
        ))}
      {!props.contract.signedOutsidePok &&
        !props.notSavedTab &&
        !props.readOnly &&
        (props.contract?.status === ContractStatusEnum.Entered ||
          props.contract?.status === ContractStatusEnum.Unsigned) && (
          <Button
            className="ms-2 mt-4"
            variant="primary"
            onClick={handleSaveStatusContract}
            isLoading={isLoadingStatus}
          >
            {t(tk.contract.sendToSigning)}
          </Button>
        )}
      {props.contract.signedOutsidePok &&
        !props.notSavedTab &&
        !props.readOnly &&
        (props.contract?.status === ContractStatusEnum.Entered ||
          props.contract?.status === ContractStatusEnum.Unsigned) && (
          <Button
            className="ms-2 mt-4"
            variant="primary"
            onClick={handleMarkAsSignedContract}
          >
            {t(tk.contract.markAsSigned)}
          </Button>
        )}
      {!props.notSavedTab &&
        props.contract.status === ContractStatusEnum.WaitingForSigning &&
        props.extendedContract?.authorizedOwner?.id ===
          auth.currentUser?.sub && (
          <>
            <ConfirmationButton
              variant="outline-danger"
              className="mb-4 me-1 mt-4"
              confirmation={t(tk.contract.contractRejection)}
              reasonText={t(tk.contract.rejectionContractReason)}
              confirmLabel={t(tk.contract.rejectContract)}
              errorLabel={t(tk.contract.fillRejectionReason)}
              rejectLabel={t(tk.button.cancel)}
              dynamicInput
              askForAReason
              reasonNotEmpty
              focusInput
              onOK={unsignContracts}
              isLoading={isLoadingSigning}
            >
              {t(tk.contract.unsignContract)}
            </ConfirmationButton>
            <ConfirmationButton
              variant="outline-primary"
              className="mb-4 me-1 mt-4"
              onOK={() => signContracts()}
              confirmation={t(tk.contract.askSigningContracts)}
              isLoading={isLoadingSigning}
            >
              {t(tk.contract.signContract)}
            </ConfirmationButton>
          </>
        )}
      {props.contract.status === ContractStatusEnum.Signed &&
        (props.contract?.notSendAutomatically ||
          props.contract?.signedOutsidePok) &&
        !props.contract?.sentByHand && (
          <>
            <ConfirmationButton
              variant="outline-primary"
              className="mb-4 me-1 mt-4"
              onOK={() => markSentByHand()}
              confirmation={t(tk.contract.askSentByHandsContract)}
              isLoading={isLoadingSigning}
            >
              {t(tk.contract.sentByHand)}
            </ConfirmationButton>
          </>
        )}
    </div>
  );
};
